import { useCallback, useEffect, useRef, type MouseEvent } from "react";
import { useTasksContext } from "./tasks.context";
import styles from "./SelectedTaskContainer.module.scss";

const FALLBACK_CLEANUP_MS = 300;

/**
 * Slides the task pane in or out when its toggle button is clicked. A class on
 * the panel group turns on a flex-grow transition for that one toggle, so
 * dragging the resize handle never animates. The pane's content keeps its
 * widest pixel width meanwhile, pinned left and clipped, so it slides instead
 * of reflowing. A keyboard click (`detail === 0`) opens without the entrance.
 * A toggle mid-slide first ends the slide in progress.
 */
export const useTaskPaneMotion = () => {
    const { taskDetailPanelRef: paneRef } = useTasksContext();
    const groupElementRef = useRef<HTMLDivElement>(null);
    const paneContentRef = useRef<HTMLDivElement>(null);
    const finishPendingRef = useRef<(() => void) | null>(null);

    useEffect(() => () => finishPendingRef.current?.(), []);

    const animateToggle = useCallback(
        (event: MouseEvent, toggle: () => void) => {
            finishPendingRef.current?.();
            const group = groupElementRef.current;
            const content = paneContentRef.current;
            const pane = paneRef.current;
            const paneElement = content?.closest<HTMLElement>("[data-panel]");
            const isExpanding = pane?.isCollapsed() ?? false;
            if (
                !group ||
                !content ||
                !pane ||
                !paneElement ||
                (isExpanding && event.detail === 0)
            ) {
                toggle();
                return;
            }

            const motionClassName = isExpanding
                ? styles.paneEntering
                : styles.paneLeaving;
            const sizeBefore = pane.getSize().asPercentage;
            group.classList.add(motionClassName);
            toggle();
            const sizeAfter = pane.getSize().asPercentage;
            if (sizeAfter === sizeBefore) {
                group.classList.remove(motionClassName);
                return;
            }

            const widestSize = Math.max(sizeBefore, sizeAfter);
            content.style.width = `${Math.ceil((group.offsetWidth * widestSize) / 100)}px`;

            let fallbackTimeout = 0;
            const finish = () => {
                finishPendingRef.current = null;
                window.clearTimeout(fallbackTimeout);
                paneElement.removeEventListener(
                    "transitionend",
                    onTransitionEnd,
                );
                group.classList.remove(motionClassName);
                content.style.width = "";
            };
            const onTransitionEnd = (transitionEvent: TransitionEvent) => {
                if (
                    transitionEvent.target === paneElement &&
                    transitionEvent.propertyName === "flex-grow"
                ) {
                    finish();
                }
            };
            paneElement.addEventListener("transitionend", onTransitionEnd);
            fallbackTimeout = window.setTimeout(finish, FALLBACK_CLEANUP_MS);
            finishPendingRef.current = finish;
        },
        [paneRef],
    );

    return { groupElementRef, paneContentRef, animateToggle };
};
